/* eslint-disable no-useless-escape */
import React from 'react';
import { InteractiveExtraContent } from './InteractiveExtraContent';
import MultimediaContentTemplate from './multimediaContent-template';
import { CarouselBubbleStyled, CarouselCards } from './styled';
// eslint-disable-next-line import/no-cycle

const formatWhatsAppMarkdown = (message = '') => {
  if (!message) return '';

  let formatted = message;

  formatted = formatted.replace(/\n/g, '<br>');

  const bold = /(^|\s|\W)\*(?!\s)([^*]+?)\*(?!\w)/g;
  const italic = /(^|\s|\W)_(?!\s)([^_]+?)_(?!\w)/g;
  const strike = /(^|\s|\W)~(?!\s)([^~]+?)~(?!\w)/g;

  formatted = formatted.replace(bold, '$1<strong>$2</strong>');
  formatted = formatted.replace(italic, '$1<em>$2</em>');
  formatted = formatted.replace(strike, '$1<del>$2</del>');

  return formatted;
};

// --------------------------------------------------------------------------
// PLACEHOLDERS
// --------------------------------------------------------------------------
export const replacePlaceholders = (obj, variables) => {
  if (obj?.type === 'BODY' || obj?.type === 'HEADER') {
    let message = obj.text;

    const placeholders = message.match(/\*([^*]+)\*/g);
    if (placeholders) {
      placeholders.forEach((placeholder) => {
        const text = placeholder.slice(1, -1);
        message = message.replace(placeholder, `<strong>${text}</strong>`);
      });
    }

    const replacedMessage = message.replace(/{{\d+}}/g, (match) => {
      const index = parseInt(match.match(/\d+/)[0], 10);
      return variables?.parameters?.[index - 1]?.text || '';
    });

    return replacedMessage;
  }

  if (obj?.type === 'URL') {
    const placeholder = '{{1}}';
    const value = obj.example[0];
    let { url } = obj;
    url = url.replace(placeholder, value);
    return url;
  }

  return '';
};

// --------------------------------------------------------------------------
// HELPERS
// --------------------------------------------------------------------------
const getTemplateComponentFromContent = (content, elementName) => {
  return content.templateComponents?.find(
    (el) => el?.type?.toLowerCase() === elementName?.toLowerCase()
  );
};

const getVariablesFromContent = (content, elementName) => {
  return content.components?.find((el) => el?.type === elementName);
};

// --------------------------------------------------------------------------
// HEADER
// --------------------------------------------------------------------------
export const paintHeader = (_content, onMultimediaClick) => {
  const headerObjectToReplace = getTemplateComponentFromContent(
    _content,
    'HEADER'
  );
  if (!headerObjectToReplace) return <div />;

  const variablesToReplace = getVariablesFromContent(_content, 'header');

  let contentHeader;

  if (headerObjectToReplace?.format === 'TEXT') {
    const html = formatWhatsAppMarkdown(
      replacePlaceholders(headerObjectToReplace, variablesToReplace)
    );

    contentHeader = (
      <div className="header-template-container">
        <p
          className="header-template-text"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    );
  }

  if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerObjectToReplace?.format)) {
    contentHeader = (
      <div>
        <MultimediaContentTemplate
          document={headerObjectToReplace?.format === 'DOCUMENT'}
          image={headerObjectToReplace?.format === 'IMAGE'}
          multimediaCode={
            variablesToReplace?.parameters.length &&
            variablesToReplace.parameters[0]?.multimediaCode
          }
          multimediaFileName={
            variablesToReplace?.parameters.length &&
            variablesToReplace.parameters[0]?.multimediaFilename
          }
          multimediaId={
            variablesToReplace?.parameters.length &&
            variablesToReplace.parameters[0]?.multimediaId
          }
          onMultimediaClick={onMultimediaClick}
          video={headerObjectToReplace?.format === 'VIDEO'}
        />
      </div>
    );
  }

  if (headerObjectToReplace?.format === 'LOCATION') {
    const locationParameters = _content.components[0].parameters[0].location;
    contentHeader = (
      <div>
        <MultimediaContentTemplate
          location
          locationParameters={locationParameters}
          onMultimediaClick={onMultimediaClick}
        />
      </div>
    );
  }

  return <div>{contentHeader}</div>;
};

// --------------------------------------------------------------------------
// CAROUSEL
// --------------------------------------------------------------------------
export const paintCarousel = (_content, onMultimediaClick) => {
  const carouselObjectToReplace = getTemplateComponentFromContent(
    _content,
    'CAROUSEL'
  );

  if (!carouselObjectToReplace || !carouselObjectToReplace?.cards?.length) {
    return <div />;
  }

  const variablesToReplace = getVariablesFromContent(_content, 'carousel');
  const { cards } = carouselObjectToReplace;
  const twoFirstCards = cards.slice(0, 2);

  const cardsJsx = twoFirstCards.map((el, i) => {
    // HEADER
    const headerPart = el?.components?.find((e) => e.type === 'HEADER');
    let headerJsx = <div />;

    const headerInVariables =
      variablesToReplace?.cards?.length &&
      variablesToReplace?.cards[i]?.components.find((e) => e.type === 'header');

    if (headerPart && headerInVariables) {
      headerJsx = (
        <MultimediaContentTemplate
          image={headerPart?.format === 'IMAGE'}
          multimediaCode={
            headerInVariables?.parameters?.length &&
            headerInVariables?.parameters[0]?.multimediaCode
          }
          multimediaFileName={
            headerInVariables?.parameters?.length &&
            headerInVariables?.parameters[0]?.multimediaFilename
          }
          multimediaId={
            headerInVariables?.parameters?.length &&
            headerInVariables?.parameters[0]?.multimediaId
          }
          onMultimediaClick={onMultimediaClick}
          video={headerPart?.format === 'VIDEO'}
        />
      );
    }

    // BODY
    const bodyPart = el?.components?.find((e) => e.type === 'BODY');
    let bodyJsx = <div />;

    const bodyInVariables =
      variablesToReplace?.cards?.length &&
      variablesToReplace?.cards[i]?.components.find((e) => e.type === 'body');

    if (bodyPart) {
      const raw = replacePlaceholders(bodyPart, bodyInVariables);
      const html = formatWhatsAppMarkdown(raw);

      bodyJsx = (
        <div
          className="body-template"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    // BUTTONS
    const buttonsJsx = el?.components?.find((e) => e.type === 'BUTTONS');
    let variablesButtons;

    if (buttonsJsx) {
      variablesButtons =
        variablesToReplace?.cards?.length &&
        variablesToReplace?.cards[i]?.components.filter(
          (e) => e.type === 'button'
        );
    }

    return (
      <div key={el} className="carousel-card-container">
        <CarouselBubbleStyled>
          {headerJsx}
          {bodyJsx}
        </CarouselBubbleStyled>

        {buttonsJsx && (
          <InteractiveExtraContent
            buttonsContent={buttonsJsx}
            buttonsVariables={variablesButtons}
            disableClick
          />
        )}
      </div>
    );
  });

  return (
    <CarouselCards>
      <div className="cards-container">
        {cardsJsx[0]}
        {cardsJsx[1]}
        <div className="arrow-left">{'<'}</div>
        <div className="arrow-right">{'>'}</div>
      </div>
    </CarouselCards>
  );
};

// --------------------------------------------------------------------------
// BODY
// --------------------------------------------------------------------------
export const paintBody = (_content) => {
  const bodyObjectToReplace = getTemplateComponentFromContent(_content, 'BODY');
  const variablesToReplace = getVariablesFromContent(_content, 'body');

  const raw = replacePlaceholders(bodyObjectToReplace, variablesToReplace);
  const html = formatWhatsAppMarkdown(raw);

  return (
    <div className="body-template" dangerouslySetInnerHTML={{ __html: html }} />
  );
};

// --------------------------------------------------------------------------
// FOOTER
// --------------------------------------------------------------------------
export const paintFooter = (_content) => {
  const footerObjectToReplace = getTemplateComponentFromContent(
    _content,
    'FOOTER'
  );
  if (!footerObjectToReplace) return <div />;

  let contentFooter;

  if (footerObjectToReplace?.text) {
    contentFooter = (
      <p className="footer-template">{footerObjectToReplace?.text}</p>
    );
  }

  return <div>{contentFooter}</div>;
};
